import { useState } from 'react'
import type { ItemDaySummary } from '../api/types'
import { Amt, Card, Empty, Field, Wt } from './ui'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'
import { today } from '../lib/format'

/**
 * What each flower did on one day, and the rate that implies.
 *
 * <p>This is the evening's figure. The grower is not paid per sale - he is paid
 * for his delivery, at a rate agreed once the day has shown what the flower was
 * worth. The shopkeeper reads this and types that rate himself; nothing here
 * sets it.
 *
 * <p>The average is money over weight, never the mean of the rates. Five kilos
 * at 1,200 and one at 600 is 1,100 a kilo; the mean of the two rates is 900, and
 * paying that would quietly rob the grower of the five kilos that went high.
 *
 * <p>Given a date it follows that date - on the selling screen it tracks the
 * date being sold under. Given none it carries its own box, because the grower's
 * rate is often set the next morning for the day before.
 */
export function DayAverageByItem({
  date,
  /**
   * Changed by the screen above after it writes a sale, so these averages take
   * the new one in. Without it the card sat on whatever it had fetched when the
   * date was last picked - stale from the first sale of the morning onwards.
   */
  refreshKey,
}: {
  date?: string
  refreshKey?: number
}) {
  /*
   * Owner only, and silently absent otherwise.
   *
   * This card adds the day up - what each flower took in money. The server
   * refuses that to a counter account, so rendering it there produced a card
   * full of nothing and four refused requests behind it: the shop sees a broken
   * screen and the rule it is enforcing is nowhere on it. Not asking at all is
   * both quieter and more honest.
   */
  const { isOwner } = useAuth()
  const [own, setOwn] = useState(today())
  const on = date ?? own
  const rows = useApi<ItemDaySummary[]>(isOwner ? `/sales/by-item?from=${on}&to=${on}` : null, [on, refreshKey])

  if (!isOwner) return null

  return (
    <Card title="اِس دن کا اوسط — قسم کے حساب سے">
      <p className="muted small" style={{ marginTop: 0 }}>
        نقد اور اُدھار دونوں ملا کر۔ زمیندار کا ریٹ لگاتے وقت یہی اوسط دیکھیں۔
      </p>

      {!date && (
        <div className="row">
          <Field label="تاریخ">
            <input type="date" value={own} onChange={(e) => setOwn(e.target.value)} />
          </Field>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>پھول</th>
              <th>کتنا بکا</th>
              <th>کل رقم</th>
              <th>اوسط ریٹ</th>
            </tr>
          </thead>
          <tbody>
            {(rows.data ?? []).map((row) => (
              <tr key={row.itemId}>
                <td className="name-cell">{row.itemName}</td>
                <td className="num">
                  <Wt value={row.weight} />
                </td>
                <td className="num">
                  <Amt value={row.amount} />
                </td>
                <td className="num">
                  <strong>
                    <Amt value={row.averageRate} />
                  </strong>
                </td>
              </tr>
            ))}

            {(rows.data?.length ?? 0) === 0 && (
              <tr>
                <td colSpan={4}>
                  <Empty>اِس دن کچھ نہیں بکا۔</Empty>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="muted small" style={{ marginTop: 10 }}>
        اوسط = کل رقم ÷ کل وزن۔ ریٹوں کا اوسط نہیں — وہ غلط نکلتا ہے جب ایک ریٹ پر زیادہ مال بکا ہو
        اور دوسرے پر تھوڑا۔
      </p>
    </Card>
  )
}
